import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@ais-ui/ui-core';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useSaveMapLegendsToSettings } from '@ais-ui/websockets';
import { useReplayContext } from '../../../Replay/ReplayContext';

interface ColourShipsByProps {
  className?: string;
}

export const ColourByOptions = ({ className }: ColourShipsByProps) => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { isPlaying } = useReplayContext();
  const { mutate } = useSaveMapLegendsToSettings(isPlaying);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colourByOptions = [
    {
      value: 'no colors',
      label: messages.mapMenuCategorizeNone,
    },
    {
      value: 'LegendShipType',
      label: messages.mapMenuCategorizeShipType,
    },
    {
      value: 'LegendSourceType',
      label: messages.mapMenuCategorizeSourceType,
    },
    {
      value: 'LegendTransponderType',
      label: messages.mapMenuCategorizeTransponderType,
    },
    {
      value: 'LegendShipList',
      label: messages.mapMenuCategorizeShipList,
    },
    {
      value: 'LegendAge',
      label: messages.mapMenuCategorizeTargetAge,
    },
    {
      value: 'LegendFilter',
      label: messages.mapMenuCategorizeFilter,
    },
  ];

  const currentCategorizeByValue = useMemo(() => {
    return settings['ribbonPlugin.settings']['ship_color_state'];
  }, [settings]);

  const onColourBy = (value: string): void => {
    setIsSubmitting(true);

    let pascalCaseValue = null;
    let label = '';
    for (const element of colourByOptions) {
      if (element.value.toLowerCase() === value.toLowerCase()) {
        pascalCaseValue = element.value;
        label = element.label.toLowerCase();
        break;
      }
    }

    const shipColorState = pascalCaseValue;

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        ship_color_state: shipColorState,
      },
    };

    mutate(
      { settings: settingsToSave },
      {
        onSuccess: () => {
          toast({
            className: 'apply-color-by-success-toast',
            title: 'Success!',
            description: messages.mapMenuCategorizeByOnSuccess.replace(
              '{label}',
              label
            ),
          });
          setIsSubmitting(false);
        },
        onError: () => {
          toast({
            className: 'apply-color-by-error-toast',
            title: 'Error!',
            description: messages.mapMenuCategorizeByOnError.replace(
              '{label}',
              label
            ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      }
    );
  };

  return (
    <Select
      onValueChange={onColourBy}
      value={currentCategorizeByValue}
      disabled={isSubmitting}
    >
      <SelectTrigger id="color-by-select" className="w-[180px]">
        <SelectValue placeholder="Select colour category" />
      </SelectTrigger>
      <SelectContent>
        {colourByOptions.map((colorByOption) => (
          <SelectItem
            disabled={isSubmitting}
            className="cursor-pointer"
            key={colorByOption.value}
            value={colorByOption.value}
          >
            {colorByOption.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
